import { useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { privacyPolicyData } from '@/data/privacyPolicyData';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function PrivacyPolicyOverlay({ open, onClose }) {
  const { language } = useLanguage();
  const scrollContainerRef = useRef(null);

  const policy = privacyPolicyData[language] || privacyPolicyData.pt;

  const scrollToSection = (id) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    const target = viewport?.querySelector(`#policy-${id}`);
    if (target && viewport) {
      const offset = target.offsetTop - viewport.offsetTop;
      viewport.scrollTo({ top: offset - 16, behavior: 'smooth' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        data-testid="privacy-policy-overlay"
        className="bg-[#F9F8F6] border-[#333333]/10 max-w-2xl max-h-[90vh]"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-[#47A2A2] tracking-tight">
            {policy.title}
          </DialogTitle>
          <DialogDescription className="sr-only">{policy.version}</DialogDescription>
          <p className="text-xs font-light text-[#333333]/50 mt-1">
            {policy.lastUpdated}
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4" ref={scrollContainerRef}>
          <div className="space-y-8 py-4">
            {/* Introduction */}
            {policy.introduction && (
              <div className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
                {policy.introduction}
              </div>
            )}

            {/* Summary */}
            {policy.summaryTitle && (
              <div>
                <h3 className="text-base font-normal text-[#333333] mb-3">
                  {policy.summaryTitle}
                </h3>
                <div className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
                  {policy.summary}
                </div>
              </div>
            )}

            {/* Table of Contents */}
            {policy.tocTitle && (
              <div>
                <h3 className="text-base font-normal text-[#333333] mb-3">
                  {policy.tocTitle}
                </h3>
                <nav className="flex flex-col gap-1.5">
                  {policy.sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="text-sm font-light text-[#35589B] hover:underline text-left transition-colors duration-200"
                      data-testid={`toc-link-${section.id}`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {/* Divider */}
            <div className="w-full h-[1px] bg-[#333333]/10" />

            {/* Sections */}
            {policy.sections.map((section) => (
              <div key={section.id} id={`policy-${section.id}`}>
                <h3 className="text-base font-normal text-[#333333] mb-2">
                  {section.title}
                </h3>
                <div className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
